import { Accessor, createMemo, createResource, createSignal } from 'solid-js';
import { GetMyDashboardsData, getMyDashboards } from '../requests/evoluno';
import { getCompanyDashboards } from '../requests/evoluno/queries/getCompanyDashboards';

export type Dashboards = {
  realtime: {
    loading: boolean;
    error: unknown;
    homeDashboard: Accessor<string | undefined>;
    list: Accessor<undefined | GetMyDashboardsData['getMyProfile']['company']['realTimeSequenceOfDashboard']>,
  }
  surveys: {
    loading: boolean;
    error: unknown;
    list: Accessor<undefined | GetMyDashboardsData['getMyProfile']['company']['dashboards']>,
    selection: Accessor<GetMyDashboardsData['getMyProfile']['company']['dashboards'][0] | undefined>,
    selectionId: Accessor<string | undefined>,
    select: (id: string) => void,
  }
}

export const createMyDashboards = (userValid: Accessor<boolean>, language: Accessor<string>): Dashboards => {
  const languageSource = createMemo(
    () => userValid()
      ? language()
      : undefined
  );
  const [profile] = createResource(
    languageSource,
    async lang => {
      const data = await getMyDashboards(lang);
      return data.getMyProfile;
    });
  const realtimelist = createMemo(
    () => profile.loading || profile.error
      ? undefined
      : profile()?.company.realTimeSequenceOfDashboard
  );
  const surveys = createMemo(
    () => profile.loading || profile.error
      ? undefined
      : profile()?.company.dashboards
  );
  const homeDashboard = createMemo(
    () => profile.loading || profile.error
      ? undefined
      : profile()?.company.homeDashboard
  );
  const [selectedSurveyId, setSelectedSurveyId] = createSignal<string | undefined>();
  const selectedSurvey = createMemo(
    () => profile.loading || profile.error
      ? undefined
      : profile()?.company.dashboards.find(survey => survey._id === selectedSurveyId())
  );
  return {
    realtime: {
      list: realtimelist,
      homeDashboard,
      get error() {
        return profile.error;
      },
      get loading() {
        return profile.loading;
      }
    },
    surveys: {
      get error() {
        return profile.error;
      },
      get loading() {
        return profile.loading;
      },
      list: surveys,
      selection: selectedSurvey,
      selectionId: selectedSurveyId,
      select: setSelectedSurveyId,
    }
  };
};

export const createCompanyDashboards = (userValid: Accessor<boolean>, language: Accessor<string>, companyId: string): Dashboards => {
  const companyDashboardsArgsAccessor = createMemo(() => ({
    userValid: userValid(),
    language: language(),
  }));
  const [company] = createResource(
    companyDashboardsArgsAccessor,
    async args => {
      if(args.userValid) {
        const data = await getCompanyDashboards(companyId, args.language);
        return data.company;
      }
    });
  const realtimelist = createMemo(
    () => company.loading || company.error
      ? undefined
      : company()?.realTimeSequenceOfDashboard
  );
  const surveys = createMemo(
    () => company.loading || company.error
      ? undefined
      : company()?.dashboards
  );
  const homeDashboard = createMemo(
    () => company.loading || company.error
      ? undefined
      : company()?.homeDashboard
  );
  const [selectedSurveyId, setSelectedSurveyId] = createSignal<string | undefined>();
  const selectedSurvey = createMemo(
    () => company.loading || company.error
      ? undefined
      : company()?.dashboards.find(survey => survey._id === selectedSurveyId())
  );
  return {
    realtime: {
      list: realtimelist,
      homeDashboard,
      get error() {
        return company.error;
      },
      get loading() {
        return company.loading;
      }
    },
    surveys: {
      get error() {
        return company.error;
      },
      get loading() {
        return company.loading;
      },
      list: surveys,
      selection: selectedSurvey,
      selectionId: selectedSurveyId,
      select: setSelectedSurveyId,
    }
  };

};
